import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { useOsNavigate } from "@/components/ui/os-layout";
import { entityDeepLink, type EntityRef } from "../shared/entityDeepLink";
import { EntityPreviewBubble } from "./EntityPreviewBubble";

export interface ViewEntityButtonProps {
  entity: EntityRef;
  /** `footer` = full-width pinned action; `inline` = compact link. */
  variant?: "footer" | "inline";
  /** Override the default "View X" label. */
  label?: string;
  className?: string;
  /** Bubble placement. */
  previewPlacement?: "top" | "bottom" | "left" | "right";
}

/**
 * The single "View X" control: navigates via the canonical deep-link layer and
 * shows a hover preview of the target entity. Renders nothing when the ref is
 * not routable.
 */
export function ViewEntityButton({
  entity,
  variant = "inline",
  label,
  className,
  previewPlacement = "left",
}: ViewEntityButtonProps) {
  const osNavigate = useOsNavigate();
  const link = entityDeepLink(entity);
  if (!link) return null;

  const footer = variant === "footer";

  const button = (
    <button
      type="button"
      onClick={() => link.run({ osNavigate })}
      data-testid="audit-inspector-view-entity"
      className={clsx(
        footer
          ? "inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-600 bg-slate-800/80 px-3 py-2 text-sm font-medium text-slate-100 hover:bg-slate-700"
          : "inline-flex items-center gap-1.5 text-sm font-medium text-indigo-300 hover:text-indigo-200",
        className
      )}
    >
      <span>{label ?? link.label}</span>
      <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-[11px]" />
    </button>
  );

  return (
    <EntityPreviewBubble entity={entity} placement={previewPlacement}>
      {button}
    </EntityPreviewBubble>
  );
}
